import React, { useContext } from 'react'
import { array, string, bool, oneOf, arrayOf, shape } from 'prop-types'
import { ThemeContext } from 'styled-components'
import Image from '../../../helpers/Image/Image'
import * as Styled from './M050.styles'
import { agnostifyUrl } from '../../../helpers/utils'
import Callouts from '../../../helpers/Callouts'
import { onLinkClickFn } from '../../../helpers/ComponentHelper'
import { createGolContentDataAttributes, joinContentLinkTitles } from '../../../helpers/tracking'
import VideoRenderer from '../../functional/video-renderer/VideoRenderer'
import CustomVideo from '../../functional/video-renderer/CustomVideo.tsx'

/**
 * - M050 is an image and text component
 * - It is a versatile component with a set of elements
 * that can fit to a variety of horizontal layouts.
 * It can sit in a multi-column grid (2, 3 or 4 columns) and
 * can also sit across the full width of the page in an asymmetrical layout
 * that's reversible and repeatable in a stack in alternate left/right orientation.
 */
const M050 = ({
  bodyCopy,
  callouts,
  dropShadow,
  heading,
  textAlign,
  links,
  textPosition,
  onLinkClick,
  backgroundImageUrl,
  videoId,
  altText,
  mediaRatio,
  consumingGridType,
  positionInGrid,
  customVideo,
  imageFormat
}) => {
  const theme = useContext(ThemeContext)
  const isSainsburys = theme?.channel === 'sainsburys'
  const isBottomTextPosition = textPosition === 'bottom'
  // eslint-disable-next-line no-nested-ternary
  const imagePosition = textPosition === 'right' ? 'left' : isBottomTextPosition ? 'top' : 'right'
  const bodyCopyMarkup = () => ({ __html: bodyCopy })

  const hasMediaSection = videoId || backgroundImageUrl || customVideo?.videoLink
  const hasTextSection = heading || bodyCopy || links.length > 0
  const hasFullWidthMedia = !hasTextSection || isBottomTextPosition
  const hasFullWidthText = !hasMediaSection || isBottomTextPosition
  const hasPadding = textPosition === 'bottom' || hasFullWidthText
  const golTextPadding = isSainsburys && !isBottomTextPosition
  /** This helps us check the cases when we have a link and a button */
  const hasLink = links && links.some((link) => link.style === 'link' || link.style === undefined)
  /** This helps us check the cases when we have a button */
  const hasButton = links && links.some((link) => link.style === 'primary' || link.style === 'secondary')
  /** This is to check the cases where we have multiple buttons but no links. The two checks are used for styling. */
  const hasButtons = !hasLink && links?.length > 1

  const videoRendererProps = {
    ratio: mediaRatio,
    standalone: false,
    videoId,
    type: 'M050'
  }

  const getGOLDataAttributes = (linkTitle, linkUrl, action) => {
    // Load tracking is not required for video variants
    if (customVideo?.videoLink || videoId) {
      return {}
    }
    const positionAttribute = positionInGrid !== undefined ? `Position${positionInGrid}` : 'NA'
    return createGolContentDataAttributes(
      consumingGridType,
      `M050 - ${positionAttribute}`,
      action === 'Load' ? joinContentLinkTitles(links, [], 'M050', consumingGridType, positionAttribute) : linkTitle,
      linkUrl,
      heading,
      action,
      true
    )
  }

  const addGolTracking = (event, linkUrl, linkTitle) => {
    const dataAttributes = getGOLDataAttributes(linkTitle, linkUrl)

    Object.keys(dataAttributes).forEach((attribute) =>
      event?.currentTarget?.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const ctaClick = (event, linkTitle, linkUrl, linkTarget) => {
    if (!customVideo?.videoLink || !videoId) {
      addGolTracking(event, linkUrl, linkTitle)
    }
    onLinkClickFn(event, onLinkClick, linkUrl)
    window.open(agnostifyUrl(linkUrl), linkTarget)
  }

  const linksList = () => {
    const list = []
    links.map((link, index) => {
      const { linkName, linkTitle, linkTarget, linkUrl, style } = link

      return list.push(
        <li data-test={`m050-list-${index}-container`} key={index}>
          <Styled.Cta
            data-test={`m050-list-${index}-content`}
            target={linkTarget}
            title={linkTitle}
            kind={style || 'link'}
            forwardedAs='a'
            onClick={(e) => ctaClick(e, linkTitle, linkUrl, linkTarget)}
          >
            {linkName}
          </Styled.Cta>
        </li>
      )
    })
    return list
  }

  // image should be link if valid one first
  const image =
    links && links[0] && links[0].linkUrl
      ? [
          // eslint-disable-next-line react/jsx-indent
          <a
            target={links[0].linkTarget}
            href={agnostifyUrl(links[0].linkUrl)}
            title={links[0].linkTitle}
            key='m050-image'
            data-test='m050-image-container'
            onClick={(e) => {
              onLinkClickFn(e, onLinkClick, links[0].linkUrl)
              addGolTracking(e, links[0].linkUrl, links[0].linkTitle)
            }}
          >
            <Image imageFormat={imageFormat} alt={altText} src={backgroundImageUrl} smart />
          </a>
        ]
      : [<Image imageFormat={imageFormat} alt={altText} src={backgroundImageUrl} key='m050-image' smart />]

  return (
    <Styled.Container
      gutterValue={0}
      row='xs'
      data-test='m050-container'
      isBottomTextPosition={isBottomTextPosition}
      dropShadow={dropShadow}
      {...getGOLDataAttributes('', links?.[0]?.linkUrl, 'Load')}
    >
      {hasMediaSection && (
        <Styled.MediaWrapper
          data-test='m050-media-wrapper'
          hasFullWidthMedia={hasFullWidthMedia}
          imagePosition={imagePosition}
          xs={12}
          sm={hasFullWidthMedia ? null : 7}
        >
          {(backgroundImageUrl || customVideo?.videoLink) && (
            <Styled.MediaRatioWrapper data-test='m050-media-ratio-wrapper' mediaRatio={mediaRatio}>
              {backgroundImageUrl && image}
              {customVideo?.videoLink && (
                <CustomVideo videoLink={customVideo.videoLink} fullControls={customVideo.fullControls} type='M050' />
              )}
            </Styled.MediaRatioWrapper>
          )}
          {videoId && <VideoRenderer {...{ ...videoRendererProps }} />}
        </Styled.MediaWrapper>
      )}
      {hasTextSection && (
        <Styled.TextWrapper
          data-test='m050-text-wrapper'
          hasPadding={hasPadding}
          golPadding={golTextPadding}
          textPosition={textPosition}
          textAlign={textAlign}
          isSainsburys={isSainsburys}
          smGutterValue={hasFullWidthText ? null : 1}
          xs={12}
          sm={hasFullWidthText ? null : 5}
        >
          <Styled.TextFlex data-test='m050-text-container' dropShadow={dropShadow && hasPadding}>
            {heading && (
              <Styled.Heading
                data-test='m050-heading'
                textPosition={textPosition}
                className={isSainsburys ? 'ln-u-display-2' : undefined} // this is to mitigate the large font sizes on Luna, should be reverted after they've addressed
              >
                {heading}
              </Styled.Heading>
            )}
            {bodyCopy && (
              <Styled.RichTextWrapper data-test='m050-bodyCopy' dangerouslySetInnerHTML={bodyCopyMarkup()} />
            )}
            {callouts.length > 0 && <Callouts parentComponent='m050' callouts={callouts} textAlign={textAlign} />}
            {links && (
              <Styled.LinkList
                data-test='m050-list-container'
                hasLink={hasLink}
                hasButtons={hasButtons}
                hasButton={hasButton}
                textAlign={textAlign}
              >
                {linksList()}
              </Styled.LinkList>
            )}
          </Styled.TextFlex>
        </Styled.TextWrapper>
      )}
    </Styled.Container>
  )
}

M050.propTypes = {
  /** Optional background image url */
  backgroundImageUrl: string,
  /** Written copy that describes the Image */
  altText: string,
  /** Rich text field */
  bodyCopy: string,
  /** Array with callout values to be displayed */
  callouts: array,
  /** Information about parent type */
  consumingGridType: string,
  /** Text for the CTA */
  ctaCopy: string,
  /** Boolean attribute, if true, adds a drop shadow to the component container */
  dropShadow: bool,
  /** Optional text for heading */
  heading: string,
  /** Optional image format, eg. avif */
  imageFormat: string,
  /** Array of links to be displayed */
  links: arrayOf(
    shape({
      /** Link label */
      linkName: string,
      /** Link URL */
      linkUrl: string,
      /** Optional link title, can be seen when hovering over the CTA */
      linkTitle: string,
      /** Cta style for the links - defaults to link */
      style: oneOf(['link', 'primary', 'secondary']),
      /** Option of opening in a new tab, defaults to _blank  */
      linkTarget: oneOf(['_blank', '_self'])
    })
  ),
  /** Text alignment, defaults to left */
  textAlign: oneOf(['left', 'center']),
  /** Text position, defaults to left */
  textPosition: oneOf(['right', 'left', 'bottom']),
  /** Video id from the link */
  videoId: string,
  /** Optional custom video object */
  customVideo: shape({
    /** Optional video url */
    videoLink: string,
    /** Optional prop to check if full controls are shown */
    fullControls: bool
  }),
  /** Media ratio, defaults to 3-by-2 */
  mediaRatio: oneOf(['3-by-2', '16-by-9', '1-by-1', '3-by-4'])
}

M050.defaultProps = {
  backgroundImageUrl: '',
  altText: 'Content Image',
  bodyCopy: '',
  callouts: [],
  ctaCopy: '',
  dropShadow: false,
  heading: '',
  links: [],
  textPosition: 'left',
  textAlign: 'left',
  videoId: '',
  customVideo: {
    videoLink: '',
    fullControls: true
  },
  mediaRatio: '3-by-2'
}

export default M050
